import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import '../../scss/Charts.scss';
import { formatQuarterLabel } from '../utils/formatQuarterLabel';

const TrendLineChart = ({ data = [], periods = [] }) => {
    const containerRef = useRef();
    const svgRef = useRef();
    const tooltipRef = useRef();

    function positionTooltip(event) {
        const tooltipNode = tooltipRef.current;
        const containerRect = containerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipNode.getBoundingClientRect();

        let left = event.clientX - containerRect.left + 10;
        let top = event.clientY - containerRect.top - 40;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (event.clientX + tooltipRect.width + 20 > viewportWidth) {
            left = event.clientX - containerRect.left - tooltipRect.width - 10;
        }

        if (event.clientY - tooltipRect.height - 20 < 0) {
            top = event.clientY - containerRect.top + 20;
        }

        if (event.clientY + tooltipRect.height + 20 > viewportHeight) {
            top = event.clientY - containerRect.top - tooltipRect.height - 10;
        }

        tooltipNode.style.left = `${left}px`;
        tooltipNode.style.top = `${top}px`;
    }

    useEffect(() => {
        if (
            !Array.isArray(data) ||
            data.length === 0 ||
            !periods ||
            periods.length === 0 ||
            !containerRef.current
        )
            return;

        const container = containerRef.current;
        const { width } = container.getBoundingClientRect();
        const height = 500;
        const margin = { top: 20, right: 20, bottom: 50, left: 60 };
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        const tooltip = d3.select(tooltipRef.current);

        const x = d3.scalePoint().domain(periods).range([0, innerWidth]).padding(0.5);

        const allValues = data
            .flatMap(({ values }) => values.map((v) => v.value))
            .filter((v) => typeof v === 'number' && !isNaN(v));

        const minVal = Math.min(0, d3.min(allValues));
        const maxVal = Math.max(0, d3.max(allValues));

        const y = d3
            .scaleLinear()
            .domain([minVal, maxVal])
            .nice()
            .range([innerHeight, 0]);

        const chart = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        chart
            .append('g')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(d3.axisBottom(x).tickFormat(formatQuarterLabel))
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-weight', 'bold')
            .style('text-anchor', 'end')
            .attr('transform', 'rotate(-45)');

        const yTicks = y.ticks();
        if (!yTicks.includes(0)) yTicks.push(0);
        yTicks.sort((a, b) => a - b);

        chart
            .append('g')
            .call(d3.axisLeft(y).tickValues(yTicks))
            .selectAll('text')
            .style('font-weight', 'bold')
            .style('font-size', '12px');

        const line = d3
            .line()
            .defined((d) => typeof d.value === 'number' && !isNaN(d.value))
            .x((d) => x(d.quarter))
            .y((d) => y(d.value));

        const lineGroups = new Map();

        data.forEach(({ mdrm, values }) => {
            if (!values || !Array.isArray(values)) return;
            const orderedValues = periods.map(
                (p) => values.find((v) => v.quarter === p) || { quarter: p, value: NaN },
            );
            const key = orderedValues.map((d) => `${d.quarter}:${d.value}`).join('|');

            if (!lineGroups.has(key)) {
                const firstColor = values[0]?.color || '#333';
                const safeColor =
                    firstColor.toLowerCase() === '#ffffff' ||
                        firstColor.toLowerCase() === '#fff'
                        ? '#ddd'
                        : firstColor;
                lineGroups.set(key, { mdRMs: [], data: orderedValues, color: safeColor });
            }
            lineGroups.get(key).mdRMs.push(mdrm);
        });

        lineGroups.forEach(({ mdRMs, data: orderedValues, color }) => {
            const path = chart
                .append('path')
                .datum(orderedValues)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 2)
                .style('cursor', 'pointer')
                .attr('d', line)
                .on('mouseover', (event) => {
                    path.attr('stroke-width', 4);

                    const [xPos, yPos] = d3.pointer(event);
                    const tooltipHtml = `
            <strong>${mdRMs.join(', ')}</strong><br/>
            ${orderedValues
                            .map((d) =>
                                d.value !== undefined && !isNaN(d.value)
                                    ? `${formatQuarterLabel(d.quarter)}: ${d.value}`
                                    : '',
                            )
                            .filter(Boolean)
                            .join('<br/>')}
          `;

                    tooltip
                        .style('opacity', 1)
                        .html(tooltipHtml);
                    positionTooltip(event);
                })
                .on('mousemove', (event) => {
                    positionTooltip(event);
                })
                .on('mouseout', () => {
                    path.attr('stroke-width', 2);
                    tooltip.style('opacity', 0);
                });

            chart
                .selectAll(`.point-${mdRMs.join('_')}`)
                .data(
                    orderedValues.filter((d) => d.value !== undefined && !isNaN(d.value)),
                )
                .enter()
                .append('circle')
                .attr('cx', (d) => x(d.quarter))
                .attr('cy', (d) => y(d.value))
                .attr('r', 3)
                .attr('fill', color)
                .attr('stroke', '#fff')
                .attr('stroke-width', 0.8)
                .style('pointer-events', 'none');
        });
    }, [data, periods]);

    return (
        <div ref={containerRef} className="trendline-container">
            <svg ref={svgRef} />
            <div ref={tooltipRef} className="trendline-tooltip" />
        </div>
    );
};

export default TrendLineChart;
